import { DEFAULT_COUNTRY_CALLING_CODE } from 'data/common';
import { COUNTRY_CALLING_CODE_OPTIONS } from 'utils/countries';
import getPhoneNumberObject from 'utils/getPhoneNumberObject';

/**
 * Extracts the country calling code with a '+' prefix from the given phone number.
 * If the phone number cannot be parsed, it returns the default country calling code.
 *
 * @param phoneNumber - The full phone number in E.164 format.
 * @returns The country calling code with a '+' prefix (e.g., '+1'), or the default if parsing fails.
 */
export function getCountryCallingCodeWithPrefix(phoneNumber: string): string {
  const parsedPhoneNumber = getPhoneNumberObject(phoneNumber);

  if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
    // Return the country calling code with a '+' prefix
    return `+${parsedPhoneNumber.countryCallingCode}`;
  }

  // If parsing fails, attempt to manually extract the country calling code
  for (const option of COUNTRY_CALLING_CODE_OPTIONS) {
    if (phoneNumber.startsWith(option.value)) {
      return option.value; // Return the matched country calling code
    }
  }

  // If no match found, fallback to the original regex approach
  const match = phoneNumber.match(/^\+(\d{1,3})/); // Match +1 to +999
  return match ? match[0] : DEFAULT_COUNTRY_CALLING_CODE; // Return the matched part or the default
}

/**
 * Extracts the national (local) phone number from the given phone number.
 *
 * @param phoneNumber - The full phone number in E.164 format.
 * @returns The national (local) phone number as a string, or undefined if parsing fails.
 */
export function getPhoneNationalNumber(phoneNumber: string): string | undefined {
  const parsedPhoneNumber = getPhoneNumberObject(phoneNumber);

  if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
    return parsedPhoneNumber.nationalNumber;
  }

  // If parsing fails, strip the country calling code (if any) and return the rest.
  const countryCodePrefix = getCountryCallingCodeWithPrefix(phoneNumber);
  return phoneNumber.startsWith(countryCodePrefix)
    ? phoneNumber.replace(countryCodePrefix, '').trim()
    : phoneNumber;
}
